import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { NotificationManager, SessionStorageManager, useApplicationState } from '@atlas/core';

import resolveSelfRegulationPopupVisibility from './resolveSelfRegulationPopupVisibility';
import resolveSpentLimitPopupVisibility from './resolveSpentLimitPopupVisibility';

const SPEND_LIMIT_POPUP_STORAGE_KEY = 'showSpentLimitPopup';
const REALITY_CHECK_POPUP_STORAGE_KEY = 'showRealityCheckPopup';

const SHOW_POPUP = {
  NO: 'no',
  YES: 'yes',
};

export const SelfRegulationContext = createContext({
  markSelfRegulationAsSet: () => {},
  markSpentLimitAsSet: () => {},
  showSelfRegulationPopup: false,
  showSelfRegulationPopupIfNeeded: () => Promise.resolve(false),
  showSpentLimitPopup: false,
  showSpentLimitPopupIfNeeded: () => Promise.resolve(false),
});

const SelfRegulationProvider = ({ children }) => {
  const { account, authenticated } = useApplicationState();
  const [showSpentLimitPopup, setShowSpentLimitPopup] = useState(false);
  const [showSelfRegulationPopup, setShowSelfRegulationPopup] = useState(false);

  useEffect(() => {
    Promise.all([
      resolveSpentLimitPopupVisibility(account, authenticated),
      resolveSelfRegulationPopupVisibility(account, authenticated),
    ]).then(([shouldShowSpentLimit, shouldShowSelfRegulation]) => {
      setShowSpentLimitPopup(shouldShowSpentLimit);
      setShowSelfRegulationPopup(shouldShowSelfRegulation);
      SessionStorageManager.setItem(
        SPEND_LIMIT_POPUP_STORAGE_KEY,
        shouldShowSpentLimit ? SHOW_POPUP.YES : SHOW_POPUP.NO,
      );
      SessionStorageManager.setItem(
        REALITY_CHECK_POPUP_STORAGE_KEY,
        shouldShowSelfRegulation ? SHOW_POPUP.YES : SHOW_POPUP.NO,
      );
    });
  }, [account, authenticated]);

  const markSpentLimitAsSet = useCallback(() => {
    SessionStorageManager.setItem(SPEND_LIMIT_POPUP_STORAGE_KEY, SHOW_POPUP.NO);
    setShowSpentLimitPopup(false);
  }, []);

  const markSelfRegulationAsSet = useCallback(() => {
    SessionStorageManager.setItem(REALITY_CHECK_POPUP_STORAGE_KEY, SHOW_POPUP.NO);
    setShowSelfRegulationPopup(false);
  }, []);

  const showSpentLimitPopupIfNeeded = useCallback(() => {
    return new Promise(resolve => {
      if (SessionStorageManager.getItem(SPEND_LIMIT_POPUP_STORAGE_KEY) === SHOW_POPUP.NO) {
        resolve(true);
        setShowSpentLimitPopup(false);
        return;
      }

      if (showSpentLimitPopup) {
        NotificationManager.display({
          data: {
            onClosePopup: () => {
              SessionStorageManager.setItem(SPEND_LIMIT_POPUP_STORAGE_KEY, SHOW_POPUP.YES);
              setShowSpentLimitPopup(true);
              resolve(false);
            },
            onSuccessPopup: () => {
              markSpentLimitAsSet();
              resolve(true);
            },
          },
          id: 'spentLimit',
          type: 'spentLimit',
        });
        return;
      } else {
        resolve(true);
        setShowSpentLimitPopup(false);
        return;
      }
    });
  }, [markSpentLimitAsSet, showSpentLimitPopup]);

  const showSelfRegulationPopupIfNeeded = useCallback(() => {
    return new Promise(resolve => {
      if (SessionStorageManager.getItem(REALITY_CHECK_POPUP_STORAGE_KEY) === SHOW_POPUP.NO) {
        resolve(true);
        setShowSelfRegulationPopup(false);
        return;
      }

      if (showSelfRegulationPopup) {
        NotificationManager.display({
          data: {
            onClosePopup: () => {
              SessionStorageManager.setItem(REALITY_CHECK_POPUP_STORAGE_KEY, SHOW_POPUP.YES);
              setShowSelfRegulationPopup(true);
              resolve(false);
            },
            onSuccessPopup: () => {
              markSelfRegulationAsSet();
              resolve(true);
            },
          },
          id: 'realityCheckTimeLimitsPopup',
          type: 'realityCheckTimeLimitsPopup',
        });
      } else {
        resolve(true);
        setShowSelfRegulationPopup(false);
      }
    });
  }, [markSelfRegulationAsSet, showSelfRegulationPopup]);

  const state = useMemo(
    () => ({
      markSelfRegulationAsSet,
      markSpentLimitAsSet,
      setShowSelfRegulationPopup,
      showSelfRegulationPopup,
      showSelfRegulationPopupIfNeeded,
      showSpentLimitPopup,
      showSpentLimitPopupIfNeeded,
    }),
    [
      markSelfRegulationAsSet,
      markSpentLimitAsSet,
      showSelfRegulationPopup,
      showSelfRegulationPopupIfNeeded,
      showSpentLimitPopup,
      showSpentLimitPopupIfNeeded,
    ],
  );

  return <SelfRegulationContext.Provider value={state}>{children}</SelfRegulationContext.Provider>;
};

SelfRegulationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useSelfRegulationContext = () => useContext(SelfRegulationContext);

export default SelfRegulationProvider;
